/**
 * Sandbox policy for the plugin iframe — the SINGLE source of truth shared by
 * every surface that mounts a plugin (the production renderer plus the builder
 * and dev preview panes). One constant guarantees a plugin behaves identically
 * in preview and in production; drift would let a capability appear on one
 * surface and not another.
 *
 * Tokens (order is cosmetic — the attribute is a set):
 *   - allow-scripts   — plugins are JS apps.
 *   - allow-forms     — plugins submit forms.
 *   - allow-downloads — plugins can trigger downloads (exports, generated files).
 *   - allow-popups + allow-popups-to-escape-sandbox — a plugin may open an
 *     external link in a new tab (e.g. `window.open(url, '_blank')` behind a
 *     button click). Without allow-popups the browser silently no-ops the call;
 *     allow-popups-to-escape-sandbox makes the opened tab a normal browsing
 *     context so the destination (YouTube, docs, …) isn't itself sandboxed and
 *     broken.
 *
 * SECURITY (accepted trade-off): with allow-popups a plugin can navigate the
 * user to ANY external URL with no host mediation and no confirmation — a
 * native `window.open` never reaches the host, so the platform cannot
 * gate/allowlist it at runtime. Outbound navigation is instead policed by the
 * plugin security-review pipeline (services/plugin-review-service), whose AI
 * analyzer is primed to scrutinize `window.open` targets.
 *
 * Deliberately ABSENT — never add either to a production surface:
 *   - allow-same-origin      → would let a plugin escape its opaque origin and
 *                              read/write cross-frame (defeats isolation).
 *   - allow-top-navigation*  → would let a plugin hijack the whole Fias tab.
 *
 * The dev harness (packages/plugin-dev-harness/.../harness.html) intentionally
 * runs a LOOSER sandbox (it adds allow-same-origin for local tooling). That is
 * a localhost-only convenience and must not be mirrored here.
 */
export const PLUGIN_IFRAME_SANDBOX =
  'allow-scripts allow-forms allow-downloads allow-popups allow-popups-to-escape-sandbox';

/**
 * Permissions Policy delegated to the plugin frame — the `allow` attribute.
 *
 * `autoplay` is here because without it a plugin cannot play audio AT ALL.
 * The feature defaults to `self`, and a sandbox without `allow-same-origin`
 * gives the frame an opaque origin, which is cross-origin to us — so the
 * feature is disabled inside it and `HTMLMediaElement.play()` rejects with
 * NotAllowedError. Crucially that is a POLICY denial, not the ordinary
 * autoplay heuristic: a user gesture inside the frame does not lift it, so
 * the usual "park the track and start it on first interaction" recovery just
 * fails forever. Flappyland's music was silent on staging for exactly this
 * reason while its sound effects (which fall back to a Web Audio synth) still
 * played — Tom, 2026-08-02.
 *
 * The trade-off accepted: a plugin can now start audio without waiting for a
 * gesture. That is an annoyance a user can leave, not a capability — it
 * grants no access to anything, and the browser's own autoplay heuristics
 * still apply on top. Sound is table stakes for the games the platform hosts.
 *
 * Keep this MINIMAL for the same reason as the sandbox above: every feature
 * named here is one the plugin gets and cannot otherwise have. Nothing that
 * touches hardware or location (`camera`, `microphone`, `geolocation`,
 * `display-capture`) belongs here — those are capabilities, and capabilities
 * are Entities, not iframe attributes.
 */
export const PLUGIN_IFRAME_ALLOW = 'autoplay';
